import { useQueryClient } from "@tanstack/react-query";
import { Ban, CircleAlert, LoaderCircle } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useOpenArtefact } from "../../router.tsx";
import { KindIcon } from "./kind.tsx";
import type { ArtefactCardState } from "./parts.ts";
import { type ConversationScope, refreshArtefacts, useArtefacts } from "./queries.ts";

/**
 * Where the agent created or revised an artefact (design §2): one hairline row with the kind,
 * title and version, and "Open", which shows that version beside the conversation. While the
 * agent writes, the row is dashed ("not yet") and says so politely, without previewing content.
 * A version the viewer may not read says so, and nothing more.
 */
export function ArtefactCard(props: {
  card: ArtefactCardState;
  scope: ConversationScope | null;
  /** Its message is still being written; a write left unfinished otherwise failed. */
  streaming: boolean;
}) {
  const { scope, streaming } = props;
  const card: ArtefactCardState =
    props.card.status === "writing" && !streaming
      ? { status: "failed", title: props.card.title }
      : props.card;
  const { t } = useTranslation();
  if (card.status === "writing") {
    return (
      <CardFrame dashed>
        <Tile>
          <LoaderCircle
            aria-hidden="true"
            strokeWidth={1.75}
            className="size-4 animate-spin text-fg-muted motion-reduce:animate-none"
          />
        </Tile>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-fg">{card.title ?? t("artefacts.untitled")}</p>
          <p className="text-xs text-fg-muted" role="status" aria-live="polite">
            {t("artefacts.writing")}
          </p>
        </div>
      </CardFrame>
    );
  }
  if (card.status === "failed") {
    return (
      <CardFrame>
        <Tile>
          <CircleAlert aria-hidden="true" strokeWidth={1.75} className="size-4 text-danger-fg" />
        </Tile>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-fg">{card.title ?? t("artefacts.untitled")}</p>
          <p className="text-xs text-danger-fg">{t("artefacts.failed")}</p>
        </div>
      </CardFrame>
    );
  }
  return scope ? (
    <WrittenCard card={card} scope={scope} />
  ) : (
    <CardFrame>
      <Tile>
        <KindIcon kind={card.kind} className="text-fg-secondary" />
      </Tile>
      <CardText card={card} />
    </CardFrame>
  );
}

function WrittenCard(props: {
  card: Extract<ArtefactCardState, { status: "done" }>;
  scope: ConversationScope;
}) {
  const { card, scope } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const list = useArtefacts(scope);
  const { open, show } = useOpenArtefact();
  const summary = list.data?.artefacts.find((a) => a.handle === card.handle);
  // A card naming a version the cached list lacks: the list is read once more (design §3).
  const behind = list.isSuccess && (!summary || summary.version < card.version);
  const refetched = useRef(false);
  useEffect(() => {
    if (behind && !refetched.current) {
      refetched.current = true;
      refreshArtefacts(queryClient, scope.conversationId);
    }
  }, [behind, queryClient, scope.conversationId]);
  const settled = list.isSuccess && !list.isFetching && refetched.current;
  // Not readable: no artefact by that handle for this viewer, or not this version of it.
  const unavailable = (behind && settled) || (list.isError && !list.data);
  if (unavailable) {
    return (
      <CardFrame muted>
        <Tile>
          <Ban aria-hidden="true" strokeWidth={1.75} className="size-4 text-fg-muted" />
        </Tile>
        <p className="min-w-0 flex-1 text-fg-muted">{t("artefacts.unavailable")}</p>
      </CardFrame>
    );
  }
  const shown =
    summary !== undefined &&
    open?.artefactId === summary.id &&
    (open.version ?? summary.version) === card.version;
  return (
    <CardFrame selected={shown}>
      <Tile>
        <KindIcon kind={card.kind} className="text-fg-secondary" />
      </Tile>
      <CardText card={card} />
      <Button
        variant="outline"
        size="sm"
        disabled={!summary || behind}
        aria-label={t("artefacts.openNamed", { title: card.title })}
        onClick={() => summary && show({ artefactId: summary.id, version: card.version })}
      >
        {t("artefacts.open")}
      </Button>
    </CardFrame>
  );
}

function CardText({ card }: { card: Extract<ArtefactCardState, { status: "done" }> }) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 flex-1">
      <p className="truncate font-medium text-fg">{card.title}</p>
      <p className="text-xs text-fg-muted tabular-nums">
        {t(`artefacts.kind.${card.kind}`)} · {t("artefacts.version", { version: card.version })}
      </p>
    </div>
  );
}

function CardFrame(props: {
  children: ReactNode;
  dashed?: boolean;
  muted?: boolean;
  selected?: boolean;
}) {
  return (
    <div
      data-esi-content="files"
      data-artefact-card=""
      className={cn(
        "flex max-w-md items-center gap-3 rounded-lg border px-3 py-2.5 text-sm",
        props.dashed
          ? "border-dashed border-line-strong bg-transparent"
          : props.muted
            ? "border-line bg-surface-sunken"
            : props.selected
              ? "border-line-strong bg-surface-selected/60"
              : "border-line bg-surface-raised",
      )}
    >
      {props.children}
    </div>
  );
}

function Tile({ children }: { children: ReactNode }) {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-sunken">
      {children}
    </span>
  );
}
